import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { ApiService } from "../../services/api";
import checkPermission from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import SectionManager from "./components/SectionManager";
import ParticipantManager from "./components/ParticipantManager";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";

const SURVEY_ACTION_OPTIONS = [
  { value: "NONE", label: "Hiçbir şey" },
  { value: "ASSIGN_FIXED_DEPARTMENT", label: "Departmana ata" },
  { value: "ASK_DEPARTMENT_ON_REVIEW", label: "Departman seçimi iste" },
  { value: "DELETE_USER", label: "Kullanıcının hesabını sil" },
  { value: "DEACTIVATE_USER", label: "Kullanıcının hesabını pasife al" },
];

export default function NewSurveyPage() {
  const navigate = useNavigate();
  const hasCreatePermission = checkPermission("survey_add");
  const { showAlert } = useModal();

  useEffect(() => {
    if (!hasCreatePermission) {
      navigate("/surveys");
    }
  }, [hasCreatePermission, navigate]);

  const toDateTimeLocal = (date: Date) => {
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  const now = new Date();
  const oneWeekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "SURVEY",
    isActive: true,
    startDate: toDateTimeLocal(now),
    endDate: toDateTimeLocal(oneWeekLater),
    languages: ["tr"],
    pagination: "PAGINATION",
    processType: "PERCENTAGE",
    isActionEnabled: false,
    approveActionType: "NONE",
    rejectActionType: "NONE",
    approveDepartmentId: "",
    rejectDepartmentId: "",
    metadata: {},
  });

  const [isLoading, setIsLoading] = useState(false);
  const [currentSurveyId, setCurrentSurveyId] = useState<string | null>(null);
  const [reviewDepartments, setReviewDepartments] = useState<Array<{ id: string; name: string; color?: string; parentId?: string | null }>>([]);
  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-emerald-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  useEffect(() => {
    const loadReviewDepartments = async () => {
      try {
        const departments = await ApiService.getMySurveyReviewDepartments();
        setReviewDepartments(Array.isArray(departments) ? departments : []);
      } catch (error) {
        console.error("Review departmanları yüklenemedi:", error);
        setReviewDepartments([]);
      }
    };

    loadReviewDepartments();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    try {
      setIsLoading(true);
      const payload: any = {
        type: form.type,
        title: form.title,
        startDate: form.startDate,
        endDate: form.endDate,
        languages: form.languages,
        isActive: form.isActive,
        pagination: form.pagination,
        processType: form.processType,
        isActionEnabled: form.isActionEnabled,
        approveActionType: form.isActionEnabled ? form.approveActionType : "NONE",
        rejectActionType: form.isActionEnabled ? form.rejectActionType : "NONE",
        approveDepartmentId:
          form.isActionEnabled && form.approveActionType === "ASSIGN_FIXED_DEPARTMENT"
            ? form.approveDepartmentId || null
            : null,
        rejectDepartmentId:
          form.isActionEnabled && form.rejectActionType === "ASSIGN_FIXED_DEPARTMENT"
            ? form.rejectDepartmentId || null
            : null,
        metadata: form.metadata,
      };

      if (
        form.isActionEnabled &&
        form.approveActionType === "ASSIGN_FIXED_DEPARTMENT" &&
        !form.approveDepartmentId
      ) {
        await showAlert({
          title: "Uyarı",
          message: "Onay aksiyonu için bir departman seçmelisiniz.",
          type: "warning",
        });
        return;
      }

      if (
        form.isActionEnabled &&
        form.rejectActionType === "ASSIGN_FIXED_DEPARTMENT" &&
        !form.rejectDepartmentId
      ) {
        await showAlert({
          title: "Uyarı",
          message: "Ret aksiyonu için bir departman seçmelisiniz.",
          type: "warning",
        });
        return;
      }

      if (form.description && form.description.trim()) {
        payload.description = form.description.trim();
      }

      if (!form.startDate || !form.endDate) {
        await showAlert({
          title: "Uyarı",
          message: "Başlangıç ve bitiş tarihi zorunludur.",
          type: "warning",
        });
        return;
      }

      if (new Date(form.endDate).getTime() <= new Date(form.startDate).getTime()) {
        await showAlert({
          title: "Uyarı",
          message: "Bitiş tarihi başlangıç tarihinden sonra olmalıdır.",
          type: "warning",
        });
        return;
      }

      const res = await ApiService.createSurvey(payload);
      const surveyId = res?.data?.id || res?.id;

      if (surveyId) {
        setCurrentSurveyId(surveyId);
      }
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error.message || "Anket oluşturulamadı.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = () => {
    navigate("/surveys");
  };

  return (
    <>
      <div className="min-h-full bg-gray-100 flex flex-col">
        <div className="flex-1 flex flex-col">
          <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
            <div className="px-6 py-3">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">Anketler</span>
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <button
                    onClick={() => navigate("/surveys")}
                    className="pb-3 transition-all duration-200 text-emerald-600 font-medium border-b-2 border-emerald-500">
                    Tüm Anketler
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <div className="flex-1 p-5 sm:p-6">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Yeni Anket</h1>
                    <p className="text-sm text-gray-600 mt-1">Anket bilgilerini doldurun ve sorular ekleyin.</p>
                    <div className="mt-3 h-1 w-20 rounded-full bg-emerald-200" />
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/surveys")}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                    Geri Dön
                  </button>
                </div>

                {currentSurveyId && (
                  <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-green-700 text-sm">
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="font-medium">Anket Başarıyla Oluşturuldu</span>
                  </div>
                )}

                <form onSubmit={handleSave} className="space-y-5">
                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Temel Bilgiler</span>
                    </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Anket Başlığı *</label>
                    <input
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      className={fieldClassName}
                      placeholder="Örn: 2025 Çalışan Memnuniyet Anketi"
                      required
                      disabled={Boolean(currentSurveyId)}
                    />
                  </div>

                  </div>

                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Açıklama</span>
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      rows={4}
                      className={fieldClassName}
                      placeholder="Örn: Bu anket, çalışanlarımızın iş ortamı, yönetim ve kariyer gelişimi hakkındaki görüşlerini almak amacıyla hazırlanmıştır."
                    />
                    </div>
                  </div>

                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Tarih Aralığı</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi *</label>
                      <input
                        type="datetime-local"
                        name="startDate"
                        value={form.startDate}
                        onChange={handleChange}
                        className={fieldClassName}
                        required
                        disabled={Boolean(currentSurveyId)}
                      />
                      </div>
                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi *</label>
                      <input
                        type="datetime-local"
                        name="endDate"
                        value={form.endDate}
                        onChange={handleChange}
                        className={fieldClassName}
                        required
                        disabled={Boolean(currentSurveyId)}
                      />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">Sayfalama Yöntemi</label>
                      <div className="space-y-3">
                        <label
                          className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            form.pagination === "PAGINATION"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}>
                          <input
                            type="radio"
                            name="pagination"
                            value="PAGINATION"
                            checked={form.pagination === "PAGINATION"}
                            onChange={handleChange}
                            className="mt-0.5 w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 text-sm">Bölüm Başına Bir Sayfa</div>
                            <div className="text-xs text-gray-500 mt-1">Her bölüm ayrı sayfada gösterilir.</div>
                          </div>
                        </label>
                        <label
                          className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            form.pagination === "SCROLL" ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}>
                          <input
                            type="radio"
                            name="pagination"
                            value="SCROLL"
                            checked={form.pagination === "SCROLL"}
                            onChange={handleChange}
                            className="mt-0.5 w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 text-sm">Kaydırma</div>
                            <div className="text-xs text-gray-500 mt-1">Tüm bölümler tek sayfada gösterilir.</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">İlerleme Gösterimi</label>
                      <div className="space-y-3">
                        <label
                          className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            form.processType === "PERCENTAGE"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}>
                          <input
                            type="radio"
                            name="processType"
                            value="PERCENTAGE"
                            checked={form.processType === "PERCENTAGE"}
                            onChange={handleChange}
                            className="mt-0.5 w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 text-sm">Yüzde</div>
                            <div className="text-xs text-gray-500 mt-1">İlerleme yüzde olarak gösterilir (%75).</div>
                          </div>
                        </label>
                        <label
                          className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            form.processType === "NUMBER" ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}>
                          <input
                            type="radio"
                            name="processType"
                            value="NUMBER"
                            checked={form.processType === "NUMBER"}
                            onChange={handleChange}
                            className="mt-0.5 w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 text-sm">Sayı</div>
                            <div className="text-xs text-gray-500 mt-1">İlerleme sayı olarak gösterilir (5/10).</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Eylemli Anket</p>
                        <p className="text-xs text-gray-500">Onayla/Reddet kararlarında otomatik aksiyon uygulanır.</p>
                      </div>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.isActionEnabled}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              isActionEnabled: e.target.checked,
                              approveActionType: e.target.checked ? prev.approveActionType : "NONE",
                              rejectActionType: e.target.checked ? prev.rejectActionType : "NONE",
                              approveDepartmentId: e.target.checked ? prev.approveDepartmentId : "",
                              rejectDepartmentId: e.target.checked ? prev.rejectDepartmentId : "",
                            }))
                          }
                          className="sr-only peer"
                          disabled={Boolean(currentSurveyId)}
                        />
                        <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-emerald-600 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
                      </label>
                    </div>

                    {form.isActionEnabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Onay Aksiyonu</label>
                          <select
                            value={form.approveActionType}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                approveActionType: e.target.value,
                                approveDepartmentId:
                                  e.target.value === "ASSIGN_FIXED_DEPARTMENT" ? prev.approveDepartmentId : "",
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                            disabled={Boolean(currentSurveyId)}
                          >
                            {SURVEY_ACTION_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {form.approveActionType === "ASSIGN_FIXED_DEPARTMENT" && (
                            <DepartmentSelector
                              value={form.approveDepartmentId || ""}
                              onChange={(departmentId) =>
                                setForm((prev) => ({ ...prev, approveDepartmentId: departmentId || "" }))
                              }
                              placeholder="Onay departmanını seçin"
                              disabled={Boolean(currentSurveyId)}
                              departmentOptions={reviewDepartments}
                              openDirection="up"
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Ret Aksiyonu</label>
                          <select
                            value={form.rejectActionType}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                rejectActionType: e.target.value,
                                rejectDepartmentId:
                                  e.target.value === "ASSIGN_FIXED_DEPARTMENT" ? prev.rejectDepartmentId : "",
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                            disabled={Boolean(currentSurveyId)}
                          >
                            {SURVEY_ACTION_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {form.rejectActionType === "ASSIGN_FIXED_DEPARTMENT" && (
                            <DepartmentSelector
                              value={form.rejectDepartmentId || ""}
                              onChange={(departmentId) =>
                                setForm((prev) => ({ ...prev, rejectDepartmentId: departmentId || "" }))
                              }
                              placeholder="Ret departmanını seçin"
                              disabled={Boolean(currentSurveyId)}
                              departmentOptions={reviewDepartments}
                              openDirection="up"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {!currentSurveyId && (
                    <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => navigate(-1)}
                          className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                          İptal
                        </button>
                        <button
                          type="submit"
                          disabled={!form.title.trim() || isLoading}
                          className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-md hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60 text-sm">
                          {isLoading ? "Kaydediliyor..." : "Anket Oluştur"}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {currentSurveyId && (
                <>
                  <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                    <SectionManager surveyId={currentSurveyId} canUpdateSurvey={hasCreatePermission} />
                  </div>

                  <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                    <ParticipantManager surveyId={currentSurveyId} onComplete={handleFinish} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
